<script setup lang="ts">
import {inject, ref} from 'vue';

const props = defineProps({
  authorUsername: String,
  avatar: String,
  currency_code: String,
  total: {
    type: Number,
    required: true
  }
});
const {t} = useI18n();
const closeModal = inject<() => void>('closeModal');

const isOpen = ref(false);

// IMPORTANT: props.total is already the GROSS price (including VAT)
// We need to calculate the net price and VAT from the gross price
const taxRateValue = useTaxStore().rates?.post || 0;

// Calculate net price (subtotal) from gross price
const subtotal = computed(() => {
  if (!taxRateValue) return props.total;
  return Number((props.total / (1 + taxRateValue)).toFixed(2));
});

// Calculate VAT amount
const vat = computed(() => {
  return Number((props.total - subtotal.value).toFixed(2));
});

// Total with tax is just the original gross price
const totalWithTax = computed(() => props.total);

const vatLabel = computed(() => {
  if (!taxRateValue) return 'VAT (0%)';
  return `VAT (${(taxRateValue * 100).toFixed(0)}%)`;
});

const toggleDetails = () => {
  isOpen.value = !isOpen.value;
};

const closeDetails = (event: Event) => {
  const detailsElement = document.querySelector('#details-element');
  if (detailsElement && !detailsElement.contains(event.target as Node)) {
    isOpen.value = false;
  }
};
</script>

<template>
  <div>
    <div v-if="isOpen" class="inset-0 absolute z-[49] bg-[rgba(0,0,0,0.25)]" @click="closeDetails"></div>
    <div
      id="details-element"
      :class="{ 'clipped-section': true, 'visible-modal': isOpen }"
      style="box-shadow: 0 24px 32px rgba(0, 0, 0, .15);"
      class="bg-popover"
      @click.stop
    >
      <div
        class="grid grid-cols-[100px_auto_100px] items-center border-b-[0.5px] border-gray-400 py-3 px-6">
        <button @click="isOpen = false" class="flex items-center justify-start gap-1.5 text-gray">
          {{ t('buttons.cancel') }}
          <span class="-rotate-90 mt-0.5">
              <IconChevronRight size="14px"/>
            </span>
        </button>
        <h3 class="font-semibold text-base  text-center">{{ t('payment.summary.subtitle') }}</h3>
      </div>
      <div class="p-4 md:p-6">
        <div class="flex justify-between items-start py-4">
          <div>
            <NuxtLink class="font-semibold text-black"><span class="text-gradient">@{{ authorUsername
              }}</span></NuxtLink>
            <div class="text-gray text-xs">
              <div>{{ t('purchase.post_purchase') }}</div>
            </div>
          </div>
          <div class="text-foreground font-semibold">{{ subtotal }} {{ currency_code.toUpperCase() }}</div>
        </div>
        <div class="border-t border-gray-50">
          <div class="flex justify-between items-start py-4">
            <div class="text-foreground font-semibold">{{ t('payment.subtotal') }}</div>
            <div class="text-foreground font-semibold">{{ subtotal }} {{ currency_code.toUpperCase() }}</div>
          </div>
          <div class="flex justify-between items-start pb-4">
            <div class="text-foreground">{{ vatLabel }}</div>
            <div class="text-gray">{{ vat }} {{ currency_code.toUpperCase() }}</div>
          </div>
        </div>
        <div class="border-t border-gray-50">
          <div class="flex justify-between items-end pt-4">
            <div class="text-foreground font-semibold">{{ t('payment.total_due') }}</div>
            <div class="text-foreground text-lg font-semibold">{{ totalWithTax }} {{ currency_code.toUpperCase() }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="text-foreground py-4 md:py-6">
      <div class="flex items-center justify-center gap-3 mb-4 md:mb-6">
        <Avatar class="w-12 h-12">
          <AvatarImage :src="avatar" alt="@radix-vue"/>
        </Avatar>
        <div class="flex flex-col">
           <span class="text-gradient font-semibold text-lg">@{{ authorUsername
             }}</span>
          <span class="text-xs">{{ t('purchase.post_purchase') }}</span>
        </div>

      </div>

      <div class="flex items-end justify-center gap-1.5">
        <span class="font-semibold text-2xl">{{ total }} {{ currency_code.toUpperCase() }}</span>
      </div>
      <Button variant="outline" @click="toggleDetails" class=" w-1/2 mx-auto flex h-10 mt-3 gap-1.5 items-center">
        {{ t('buttons.view_details') }}
        <span class="rotate-90 mt-0.5"><IconChevronRight size="14px"/></span>
      </Button>
    </div>
  </div>
</template>

<style scoped>
.clipped-section {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  transform: translateY(-100%);
  opacity: 0;
  transition: transform 0.2s ease-in-out, opacity 0.2s ease-in-out;
}

.clipped-section.visible-modal {
  transform: translateY(0);
  opacity: 1;
}
</style>
